<template>
  <div class="blog-main-content">
    <BlogList class="right"/>
    <h1>Lightning Javazon Build Guide</h1>
    
    <p>The Lightning Javazon is a powerful Amazon build in Diablo 2 that excels at dealing massive area-of-effect damage with <strong>Lightning Fury</strong> and single-target damage with <strong>Charged Strike</strong>. This build is highly effective at clearing large groups of enemies and dealing significant damage to bosses.</p>

    <div>
      <img class="build-screenshot" alt="build screenshot" src="@/assets/builds/bd-javazon.png">
    </div>
    
    <h2>Recommended Gear</h2>
    <Equipments
      class="right"
      :left-weapon="weapon"
      :right-weapon="shield"
      :helm="helm"
      :armor="armor"
      :gloves="gloves"
      :belt="belt"
    />
    <h3>Weapons</h3>
    <ul>
        <li><strong>Titan’s Revenge</strong> (+2 skills, high attributes, life leech).</li>
        <li><strong>Thunderstroke</strong> (High attack speed and lightning bonuses).</li>
    </ul>
    
    <h3>Shield</h3>
    <ul>
        <li><strong>Phoenix</strong> (Fire absorb and enhanced damage).</li>
        <li><strong>Stormshield</strong> (High resistance and damage reduction).</li>
    </ul>
    
    <h3>Helmet</h3>
    <ul>
        <li><strong>Griffon’s Eye</strong> (+1 skills, lowers enemy lightning resistance).</li>
    </ul>
    
    <h3>Armor</h3>
    <ul>
        <li><strong>Enigma</strong> (Teleport for mobility).</li>
    </ul>
    
    <h3>Gloves</h3>
    <ul>
        <li><strong>Crafted 20% IAS +2 Javelin Skills Gloves</strong> (Best choice for attack speed and skill bonuses).</li>
        <li><strong>Laying of Hands</strong> (High attack speed and bonus against demons).</li>
    </ul>
    
    <h3>Belt</h3>
    <ul>
        <li><strong>Thunder God’s Vigor</strong> (Boosts lightning resistance and damage).</li>
        <li><strong>Verdungo’s Hearty Cord</strong> (Survivability boost).</li>
    </ul>
    
    <h3>Rings</h3>
    <ul>
        <li><strong>Raven Frost</strong> (Cannot be frozen, attack rating boost).</li>
        <li><strong>Life and Mana Leech Ring</strong> (Life steal and mana steal for survival).</li>
    </ul>
    
    <h3>Amulet</h3>
    <ul>
        <li><strong>Mara’s Kaleidoscope</strong> (+2 skills, all resistances).</li>
        <li><strong>Highlord’s Wrath</strong> (Attack speed and lightning damage bonus).</li>
    </ul>

    <h3>Inventory</h3>
    <ul>
        <li><strong>Crack of the Heavens</strong> – Used to break lightning immunities of monsters.</li>
    </ul>

    <div id="slot-content_mpu" class="content-ad-mpu"/>

    <h2>Skill Allocation</h2>
    <div class="m-3 skills-tab right">
      <b-tabs small content-class="m-2" class="tab-text">
        <b-tab title="Javelin" active>
          <img class="w-100" src="@/assets/builds/skills-lf-ama-1.png">
        </b-tab>
        <b-tab title="Passive & Magic">
          <img class="w-100" src="@/assets/builds/skills-lf-ama-2.png">
        </b-tab>
        <b-tab title="Bow">
          <img class="w-100" src="@/assets/builds/skills-lf-ama-3.png">
        </b-tab>
      </b-tabs>
    </div>
    <h3>Core Skills (Max 20 Points)</h3>
    <ul>
        <li><strong>Lightning Fury</strong> - Primary AOE attack.</li>
        <li><strong>Charged Strike</strong> - Main single-target attack.</li>
        <li><strong>Lightning Strike</strong> - Boosts lightning damage.</li>
        <li><strong>Power Strike</strong> - Additional synergy for more lightning damage.</li>
    </ul>

    <h3>Core Skills (As many as possible)</h3>
    <ul>
        <li><strong>Lightning Bolt</strong> - Synergy for core lightning skills.</li>
    </ul>
    
    <h3>Support Skills</h3>
    <ul>
        <li><strong>Pierce</strong> - At least 1 point (works well with gear bonuses).</li>
        <li><strong>Dodge, Avoid, Evade</strong> - 1 point each, depending on preference.</li>
    </ul>
    
    <h2>Attribute Allocation</h2>
    <ul>
        <li><strong>Strength</strong>: Just enough for gear.</li>
        <li><strong>Dexterity</strong>: Just enough for gear.</li>
        <li><strong>Vitality</strong>: All remaining points.</li>
        <li><strong>Energy</strong>: No points.</li>
    </ul>
    
    <h2>Mercenary Selection</h2>
    <p>Recommended: Act 2 Might Aura Mercenary</p>
    <h3>Recommended Gear</h3>
    <ul>
        <li><strong>Weapon</strong>: Infinity (Reduces enemy lightning resistance).</li>
        <li><strong>Helmet</strong>: Andariel’s Visage (Attack speed and life leech).</li>
        <li><strong>Armor</strong>: Fortitude (Damage boost and resistances).</li>
    </ul>
    
    <h2>Combat Strategy</h2>
    <ol>
        <li><strong>Against Groups</strong>: Use <strong>Lightning Fury</strong> to release chain lightning from javelins for massive AOE damage.</li>
        <li><strong>Against Single Targets</strong>: Use <strong>Charged Strike</strong> for high damage against bosses and elites.</li>
        <li><strong>Survivability</strong>: Utilize high attack speed and life leech to maintain health.</li>
    </ol>

    <div>
      <img class="build-screenshot" alt="build screenshot" src="@/assets/builds/bd-javazon-2.png">
    </div>
    
    <p>The Lightning Javazon is an extremely powerful build capable of handling high-level content with ease. With the right gear, it can efficiently clear large groups of enemies while maintaining high survivability.</p>

  </div>
</template>

<script>
import BlogList from '../BlogList.vue'
import Equipments from './Equipments.vue'

export default {
  name: 'LightningJavazon',

  components: {
    BlogList,
    Equipments,
  },

  computed: {
    weapon () {
      return {
        'img': 'eq-maiden-javelin',
        'width': 40,
        'title': "Titan's Revenge",
        'detail': [
          'Throw damage: 70-212',
          'Hit damage: 70-155',
          '+150-200% Enhanced Damage',
          "Adds 25-50 Damage",
          '+2 To Amazon Skill Levels',
          '5-9% Life Stolen Per Hit',
          '+30% Faster Run/Walk',
          '+20 To Strength',
          '+20 To Dexterity',
          "Replenishes Quantity [1 in 3s]",
          'Increased Stack Size',
          '+2 To Javelin and Spear Skills (Amazon Only)',
        ],
      }
    },

    shield () {
      return {
        'img': 'eq-monarch',
        'width': 80,
        'title': 'Phoenix Runeword',
        'runes': 'Vex + Vex + Lo + Jah',
        'detail': [
          '100% Chance To Cast level 40 Blaze When You Level-up', 
          '40% Chance To Cast Level 22 Firestorm On Striking',
          'Level 10-15 Redemption Aura When Equipped',
          '+350-400 Defense Vs. Missile', 
          '+350-400% Enhanced Damage',
          '-28% To Enemy Fire Resistance',
          '+50 To Life',
          '+5% To Maximum Lightning Resist',
          '+10% To Maximum Fire Resist',
          '+15-21 Fire Absorb',
        ],
      }
    },

    helm () {
      return {
        'img': 'eq-diadem',
        'width': 75,
        'title': "Griffon's Eye",
        'detail': [
          'Defense: 150-260', 
          '+100-200 Defense',
          '+1 To All Skills',
          '+25% Faster Cast Rate', 
          '-15-20% To Enemy Lightning Resistance',
          '+10-15% To Lightning Skill Damage'
        ],
      }
    },

    armor () {
      return {
        'img': 'eq-archon-plate',
        'width': 75,
        'title': 'Enigma Runeword',
        'runes': 'Jah + Ith + Ber',
        'detail': [
          '+2 To All Skills', 
          '+45% Faster Run/Walk',
          '+1 To Teleport',
          '+750-775 Defense', 
          '0-74 To Strength (Based On Character Level)',
          'Increase Maximum Life 5%',
          'Damage Reduced By 8%',
          '+14 Life After Each Kill',
          '15% Damage Taken Goes To Mana',
          '+1-99% Better Chance of Getting Magic Items (Based On Character Level)'
        ],
      }
    },

    gloves () {
      return {
        'img': 'eq-laying-of-hands',
        'width': 70,
        'title': "Laying of Hands",
        'detail': [
          'Defense: 79-87', 
          '+25 Defense',
          '+20% Increased Attack Speed',
          '+350% Damage To Demons', 
          'Fire Resist +50%',
          '10% Chance To Cast Level 3 Holy Bolt On Striking',
        ],
      }
    },

    belt () {
      return {
        'img': 'eq-girdle',
        'width': 75,
        'title': "Thundergod's Vigor",
        'detail': [
          'Defense: 137-159', 
          '+160-200% Enhanced Defense',
          'Adds 1-50 Lightning Damage',
          '5% Chance To Cast Level 7 Fist Of The Heavens When Struck', 
          '+20 Lightning Absorb',
          '10% To Maximum Lightning Resist',
          '+20 To Strength',
          '+20 To Vitality',
          '+3 To Lightning Fury (Amazon Only)',
          '+3 To Lightning Strike (Amazon Only)',
        ],
      }
    },
  }
}
</script>
